import type { RequestOptions, SearchRequest } from 'searchkit';
import { APP_OPTIONS, CONTENT_TYPES } from '../../config/constants';

/**
 * Search configuration for Searchkit
 *
 * This file contains the configuration for search attributes, facets, sorting,
 * and request options including time-based decay scoring for articles and documents.
 */

export const searchAttributes = [
  {
    field: 'page_title',
    weight: 13,
  },
  {
    field: 'article_type',
    weight: 13,
  },
  {
    field: 'taxonomy_theme_name',
    weight: 13,
  },
  {
    field: 'body_summarized',
    weight: 3,
  },
  {
    field: 'body',
    weight: 2,
  },
];

export const resultAttributes = [
  'type',
  'article_type_name',
  'poster_image',
  'document_type_name',
  'description',
  'combined_created',
  'page_title',
  'body',
  'body_summarized',
  'url',
  'image_style_uri',
];

export const facetAttributes = [
  {
    attribute: 'type',
    field: 'type.keyword',
    type: 'string',
  },
  {
    attribute: 'type_filter',
    field: 'type',
    type: 'string',
  },
  {
    attribute: 'combined_type_filter',
    field: 'combined_type_filter',
    type: 'string',
  },
  {
    attribute: 'entity_theme_names',
    field: 'entity_theme_names',
    type: 'string',
  },
  {
    attribute: 'document_type_name',
    field: 'document_type_name',
    type: 'string',
  },
  {
    attribute: 'page_title',
    field: 'page_title.keyword',
    type: 'string',
  },
  {
    attribute: 'combined_created',
    field: 'combined_created',
    type: 'numeric',
  },
];
export const sorting = {
  default: {
    field: '_score',
    order: 'desc',
  },
  _page_title_asc: {
    field: 'page_title.keyword',
    order: 'asc',
  },
  _page_title_desc: {
    field: 'page_title.keyword',
    order: 'desc',
  },
  _created_asc: {
    field: 'combined_created',
    order: 'asc',
  },
  _created_desc: {
    field: 'combined_created',
    order: 'desc',
  },
};
export const requestOptions: RequestOptions = {
  hooks: {
    beforeSearch: (requests: SearchRequest[]) => {
      requests.forEach((req) => {
        const { query } = req.body;
        delete req.body.query;

        req.body.query = {
          function_score: {
            query,
            functions: [
              // Time-based decay for articles based on publication_date
              {
                filter: {
                  match: {
                    type: CONTENT_TYPES.ARTICLE,
                  },
                },
                gauss: {
                  publication_date: {
                    origin: new Date(),
                    scale: APP_OPTIONS.parameters.searchkitDecay.articles.scale,
                    offset: APP_OPTIONS.parameters.searchkitDecay.articles.offset,
                    decay: APP_OPTIONS.parameters.searchkitDecay.articles.value,
                  },
                },
              },
              // Time-based decay for documents based on created date
              {
                filter: {
                  match: {
                    type: CONTENT_TYPES.DOCUMENT,
                  },
                },
                gauss: {
                  created: {
                    origin: new Date(),
                    scale: APP_OPTIONS.parameters.searchkitDecay.documents.scale,
                    offset: APP_OPTIONS.parameters.searchkitDecay.documents.offset,
                    decay: APP_OPTIONS.parameters.searchkitDecay.documents.value,
                  },
                },
              },
            ],
          },
        };
      });
      return new Promise((resolve) => {
        resolve(requests);
      });
    },
  },
};
